/**
 * @fileoverview Connect matter/client review history to selected portal-result inspection. Backend payment-review records own request/submission/version identity; URL parameters select a displayed result through `PaymentResultModal.js`. History inspection does not approve a review, verify new bank details, or move funds.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api';
import { AppButton } from '../shared';
import AppIcon from '../shared/AppIcon';
import PaymentResultModal from './PaymentResultModal';
import { bankingVerificationStatus, BANKING_REQUEST_CREATED } from './verificationTiers';
import './PaymentInstructionsHistory.css';



function ResultRow({ review, onOpen }) {
  const submission = review.submission;
  const banking = bankingVerificationStatus(review);
  const verified = banking.verified;
  const status = ['submitted', 'accepted', 'superseded', 'awaiting_submission', 'awaiting_client'].includes(review.status) ? banking.label : ({ rejected: 'Rejected', revoking: 'Revoking', revoked: 'Revoked' }[review.status] || review.status.replaceAll('_', ' '));
  const submittedAt = submission?.submitted_at ? new Date(submission.submitted_at) : null;
  return <article className="payment-history__row">
    <div className="payment-history__bank">
      <span className="payment-history__bank-icon"><AppIcon icon="pi pi-wallet" aria-hidden="true" /></span>
      <div>
        <div className="payment-history__bank-name">{submission?.destination.bank_name || 'Payment instructions requested'}</div>
        {submission ? <div className="payment-history__account"><span>{submission.destination.account_type}</span> · •••• {review.banking?.account_last4 || submission.destination.account_last4}</div> : null}
        {review.attached ? <div className="payment-history__source"><AppIcon icon="pi pi-link" aria-hidden="true" /> Attached from {review.matter.number}</div> : null}
      </div>
    </div>
    <div className="payment-history__status">
      <span className={`payment-history__badge${verified && !review.test_mode ? ' payment-history__badge--verified' : ''}`}>
        <AppIcon icon={verified ? 'pi pi-shield' : 'pi pi-clock'} aria-hidden="true" />{status}
      </span>
      {review.test_mode ? <span className="payment-history__sandbox">Sandbox</span> : null}
    </div>
    <div className="payment-history__action">
      <AppButton variant="secondary" icon="pi pi-eye" onClick={onOpen}>View results</AppButton>
      {submittedAt && !Number.isNaN(submittedAt.getTime()) ? <time dateTime={submission.submitted_at}>Submitted {submittedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</time> : null}
    </div>
  </article>;
}



export default function PaymentInstructionsHistory({ matterNumber }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [search, setSearch] = useSearchParams();
  const opener = useRef(null);
  const selected = search.get('payment_result');
  const clientId = search.get('client');
  useEffect(() => {
    function refreshRequest() { setRefresh(value => value + 1); }
    window.addEventListener(BANKING_REQUEST_CREATED, refreshRequest);
    return () => window.removeEventListener(BANKING_REQUEST_CREATED, refreshRequest);
  }, []);
  useEffect(

  () => {
    let cancelled = false;
    setGroups(null); setError('');
    api.get(`/matters/${encodeURIComponent(matterNumber)}/payment-reviews`).then(({ data }) => {
      if (!cancelled) setGroups(data.clients || []);
    }).catch(() => {
      if (!cancelled) setError('Payment instructions could not be loaded, or you do not have access.');
    });
    return () => { cancelled = true; };
  }, [matterNumber, refresh]);
  

  function open(event, review, client) {
    opener.current = event.currentTarget;
    const next = new URLSearchParams(search);
    next.set('payment_result', review.id); next.set('client', String(client));
    setSearch(next);
  }
  

  function close() {
    const next = new URLSearchParams(search);
    next.delete('payment_result'); next.delete('client'); setSearch(next, { replace:true });
    opener.current?.focus();
    setRefresh(n => n + 1);
  }
  const validSelection = groups?.some(g => String(g.client_id) === clientId && g.reviews.some(r => r.id === selected));
  return <section className="payment-history" aria-label="Matter payment instructions">
    {error ? <p className="payment-history__message" role="alert">{error}</p> : !groups ? <p className="payment-history__message" role="status">Loading payment instructions…</p> : !groups.length ? <p className="payment-history__message">No clients attached to this matter.</p> : groups.map(group => <section className="payment-history__client" key={group.client_id} aria-label={`Payment instructions for ${group.name}`}>
      <header className="payment-history__client-header"><AppIcon icon="pi pi-user" aria-hidden="true" /><h3>{group.name}</h3></header>
      {!group.reviews.length ? <p className="payment-history__empty">No payment instructions yet.</p> : group.reviews.map(review => <ResultRow key={review.id} review={review} onOpen={event => open(event, review, group.client_id)} />)}
    </section>)}
    {selected && groups && !validSelection ? <p role="alert">These results are not attached to this matter client.</p> : null}
    <PaymentResultModal visible={!!validSelection} onClose={close} reviewId={selected} matterNumber={matterNumber} clientId={clientId} />
  </section>;
}
